import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ErrorModalService, ErrorModalData } from '../services/error-modal.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-error-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="error-modal-overlay" *ngIf="modalData.isVisible" (click)="closeModal()">
      <div class="error-modal-content" [ngClass]="getModalClass()" (click)="$event.stopPropagation()">
        <div class="error-modal-header" [ngClass]="getHeaderClass()">
          <h3 class="error-modal-title">
            <i [class]="modalData.icon || 'fas fa-exclamation-triangle'" [ngClass]="modalData.iconColor || 'text-danger'"></i>
            {{ modalData.title }}
          </h3>
          <button type="button" class="error-modal-close" (click)="closeModal()">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="error-modal-body">
          <p class="error-message">{{ modalData.message }}</p>
          <div *ngIf="modalData.details" class="error-details" [ngClass]="getDetailsClass()">
            <div class="details-text" [innerHTML]="formatDetails(modalData.details)"></div>
          </div>
        </div>
        
        <div class="error-modal-footer">
          <button type="button" class="btn" [ngClass]="getButtonClass()" (click)="handleButtonClick()">
            {{ modalData.buttonText }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .error-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      backdrop-filter: blur(2px);
    }

    .error-modal-content {
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      max-width: 500px;
      width: 90%;
      max-height: 90vh;
      overflow-y: auto;
      animation: modalSlideIn 0.3s ease-out;
      border-top: 4px solid #e5e7eb;
    }

    .error-modal-content.success {
      border-top-color: #22c55e;
    }

    .error-modal-content.warning {
      border-top-color: #f59e0b;
    }

    .error-modal-content.info {
      border-top-color: #3b82f6;
    }

    .error-modal-content.upload {
      border-top-color: #f59e0b;
    }

    .error-modal-content.validation {
      border-top-color: #3b82f6;
    }

    .error-modal-content.server {
      border-top-color: #ef4444;
    }

    .error-modal-content.network {
      border-top-color: #f59e0b;
    }

    @keyframes modalSlideIn {
      from {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .error-modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px 16px;
      border-bottom: 1px solid #e5e7eb;
    }

    .error-modal-header.success {
      background-color: #f0fdf4;
    }

    .error-modal-header.warning {
      background-color: #fffbeb;
    }

    .error-modal-header.info {
      background-color: #eff6ff;
    }

    .error-modal-header.upload {
      background-color: #fffbeb;
    }

    .error-modal-header.validation {
      background-color: #eff6ff;
    }

    .error-modal-header.server {
      background-color: #fef2f2;
    }

    .error-modal-header.network {
      background-color: #fffbeb;
    }

    /* Subscription modal specific styling */
    .error-modal-header.warning .error-modal-title i[class*="fa-calendar-times"] {
      color: #f59e0b;
    }

    .error-modal-header.info .error-modal-title i[class*="fa-credit-card"] {
      color: #3b82f6;
    }

    .error-modal-header.info .error-modal-title i[class*="fa-star"] {
      color: #f59e0b;
    }

    .error-modal-content.warning .error-modal-footer .btn {
      background-color: #f59e0b;
      border-color: #f59e0b;
      color: white;
      font-weight: 600;
      padding: 12px 24px;
      font-size: 1rem;
    }

    .error-modal-content.warning .error-modal-footer .btn:hover {
      background-color: #d97706;
      border-color: #d97706;
    }

    .error-modal-content.info .error-modal-footer .btn {
      background-color: #3b82f6;
      border-color: #3b82f6;
      color: white;
      font-weight: 600;
      padding: 12px 24px;
      font-size: 1rem;
    }

    .error-modal-content.info .error-modal-footer .btn:hover {
      background-color: #2563eb;
      border-color: #2563eb;
    }

    /* Enhanced details styling for subscription modals */
    .error-details.info .details-text {
      line-height: 1.6;
      color: #374151;
    }

    .error-details.info .details-text br {
      margin-bottom: 4px;
    }

    .error-modal-title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .error-modal-title i {
      font-size: 1.5rem;
    }

    .error-modal-close {
      background: none;
      border: none;
      font-size: 1.25rem;
      color: #6b7280;
      cursor: pointer;
      padding: 4px;
      border-radius: 6px;
      transition: all 0.2s;
    }

    .error-modal-close:hover {
      background-color: #f3f4f6;
      color: #374151;
    }

    .error-modal-body {
      padding: 20px 24px;
    }

    .error-message {
      margin: 0 0 12px 0;
      font-size: 1rem;
      color: #374151;
      line-height: 1.5;
      font-weight: 500;
    }

    .error-details {
      background-color: #f9fafb;
      padding: 12px;
      border-radius: 6px;
      border-left: 4px solid #e5e7eb;
    }

    .error-details.success {
      background-color: #f0fdf4;
      border-left-color: #22c55e;
    }

    .error-details.warning {
      background-color: #fffbeb;
      border-left-color: #f59e0b;
    }

    .error-details.info {
      background-color: #eff6ff;
      border-left-color: #3b82f6;
    }

    .error-details.upload {
      background-color: #fffbeb;
      border-left-color: #f59e0b;
    }

    .error-details.validation {
      background-color: #eff6ff;
      border-left-color: #3b82f6;
    }

    .error-details.server {
      background-color: #fef2f2;
      border-left-color: #ef4444;
    }

    .error-details.network {
      background-color: #fffbeb;
      border-left-color: #f59e0b;
    }

    .error-modal-footer {
      padding: 16px 24px 20px;
      border-top: 1px solid #e5e7eb;
      display: flex;
      justify-content: flex-end;
    }

    .btn {
      padding: 10px 20px;
      border: none;
      border-radius: 6px;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      min-width: 100px;
    }

    .btn-primary {
      background-color: #3b82f6;
      color: white;
    }

    .btn-primary:hover {
      background-color: #2563eb;
    }

    .btn-primary:active {
      background-color: #1d4ed8;
    }

    .btn-success {
      background-color: #22c55e;
      color: white;
    }

    .btn-success:hover {
      background-color: #16a34a;
    }

    .btn-success:active {
      background-color: #15803d;
    }

    .btn-warning {
      background-color: #f59e0b;
      color: white;
    }

    .btn-warning:hover {
      background-color: #d97706;
    }

    .btn-warning:active {
      background-color: #b45309;
    }

    .btn-info {
      background-color: #3b82f6;
      color: white;
    }

    .btn-info:hover {
      background-color: #2563eb;
    }

    .btn-info:active {
      background-color: #1d4ed8;
    }

    .btn-danger {
      background-color: #ef4444;
      color: white;
    }

    .btn-danger:hover {
      background-color: #dc2626;
    }

    .btn-danger:active {
      background-color: #b91c1c;
    }

    /* Responsive adjustments */
    @media (max-width: 640px) {
      .error-modal-content {
        width: 95%;
        margin: 20px;
      }
      
      .error-modal-header,
      .error-modal-body,
      .error-modal-footer {
        padding: 16px 20px;
      }
    }
  `]
})
export class ErrorModalComponent implements OnInit, OnDestroy {
  modalData: ErrorModalData = {
    isVisible: false,
    title: 'Error',
    message: 'An error occurred',
    details: '',
    buttonText: 'OK',
    modalType: 'error',
    icon: 'fas fa-exclamation-triangle',
    iconColor: 'text-danger'
  };

  private subscription: Subscription = new Subscription();

  constructor(private errorModalService: ErrorModalService) {}

  ngOnInit(): void {
    this.subscription = this.errorModalService.errorModal$.subscribe(
      (data: ErrorModalData) => {
        this.modalData = data;
      }
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  closeModal(): void {
    this.errorModalService.hideError();
  }

  handleButtonClick(): void {
    // Check if this is a subscription modal by title
    if (this.modalData.title === 'Subscription Expired' || 
        this.modalData.title === 'Subscription Required' || 
        this.modalData.title === 'Why Subscribe?') {
      // Handle subscription modal button click
      this.errorModalService.handleSubscriptionModalButtonClick();
      this.errorModalService.hideError();
    } else {
      this.closeModal();
    }
  }

  getModalClass(): string {
    return this.modalData.modalType || 'error';
  }

  getHeaderClass(): string {
    return this.modalData.modalType || 'error';
  }

  getDetailsClass(): string {
    return this.modalData.modalType || 'error';
  }

  getButtonClass(): string {
    switch (this.modalData.modalType) {
      case 'success':
        return 'btn-success';
      case 'warning':
        return 'btn-warning';
      case 'info':
        return 'btn-info';
      case 'upload':
        return 'btn-warning';
      case 'validation':
        return 'btn-info';
      case 'server':
        return 'btn-danger';
      case 'network':
        return 'btn-warning';
      default:
        return 'btn-primary';
    }
  }

  formatDetails(details: string): string {
    if (!details) {
      return '';
    }
    return details.replace(/\n/g, '<br>');
  }
}
